import React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Bell, CheckCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import FormPageShell from '../components/Common/FormPageShell';
import PageHeader from '../components/Common/PageHeader';
import { notificationService } from '../services/api';
import { getAppHomePath } from '../constants/navigation';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../i18n/userFacingError';
import { fetchAllPages } from '../utils/apiPagination';
import QueryErrorBanner from '../components/Common/QueryErrorBanner';

function formatWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return String(iso);
  }
}

const severityDot = (s) => {
  if (s === 'danger') return 'bg-danger-500';
  if (s === 'warning') return 'bg-warning-500';
  if (s === 'success') return 'bg-success-500';
  return 'bg-info-500';
};

const Notifications = () => {
  const { t } = useTranslation('notifications');
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery(
    ['notifications-in-app'],
    async () => {
      return fetchAllPages((params) => notificationService.listInApp(params), 200);
    },
    { refetchInterval: 300_000 }
  );

  const markOne = useMutation(
    (id) => notificationService.markInAppRead(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications-in-app']);
        queryClient.invalidateQueries(['notifications-unread-count']);
      },
      onError: (e) => toast.error(userFacingError(e, 'notifications:markReadError')),
    }
  );

  const markAll = useMutation(() => notificationService.markAllInAppRead(), {
    onSuccess: () => {
      toast.success(t('markAllReadSuccess'));
      queryClient.invalidateQueries(['notifications-in-app']);
      queryClient.invalidateQueries(['notifications-unread-count']);
    },
    onError: (e) => toast.error(userFacingError(e)),
  });

  const list = Array.isArray(data) ? data : [];

  return (
    <FormPageShell>
      <PageHeader
        title={t("title")}
        description="Alertas dos agentes de fiscalização e controle, e avisos do sistema."
        breadcrumbs={[{ label: 'Início', to: getAppHomePath() }, { label: 'Notificações' }]}
      >
        <button
          type="button"
          className="btn btn-outline inline-flex items-center gap-2"
          disabled={markAll.isLoading || !list.length}
          onClick={() => markAll.mutate()}
        >
          <CheckCheck className="h-4 w-4" />
          Marcar todas como lidas
        </button>
      </PageHeader>

      <div className="mt-6 rounded-xl border border-gray-200 bg-white shadow-soft">
        <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
          <Bell className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Histórico</h2>
        </div>
        {isLoading && <p className="p-6 text-sm text-gray-600">Carregando…</p>}
        {error && (
          <div className="p-4">
            <QueryErrorBanner error={error} />
          </div>
        )}
        {!isLoading && !error && list.length === 0 && (
          <p className="p-6 text-sm text-gray-600">Nenhuma notificação no momento.</p>
        )}
        <ul className="divide-y divide-gray-100">
          {list.map((n) => (
            <li key={n.id} className="flex gap-3 p-4 hover:bg-gray-50/80">
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${severityDot(n.severity)}`} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="font-medium text-gray-900">{n.title}</h3>
                  <span className="text-xs text-gray-400">{formatWhen(n.created_at)}</span>
                </div>
                {n.agent_type ? (
                  <p className="mt-0.5 text-xs uppercase tracking-wide text-primary-600">
                    Agente: {n.agent_type}
                  </p>
                ) : null}
                <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{n.body}</p>
                <div className="mt-3 flex flex-wrap gap-3">
                  {n.link_href ? (
                    <Link to={n.link_href} className="text-sm font-medium text-primary-700 hover:underline">
                      Abrir no sistema
                    </Link>
                  ) : null}
                  {!n.read_at ? (
                    <button
                      type="button"
                      className="text-sm text-gray-600 hover:text-gray-900"
                      disabled={markOne.isLoading}
                      onClick={() => markOne.mutate(n.id)}
                    >
                      Marcar como lida
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">Lida</span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </FormPageShell>
  );
};

export default Notifications;
